import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Common";
import { initials } from "@/lib/format";
import { Plus, Mail, Phone, UserRound, Pencil, Trash2, KeyRound, Search, Power } from "lucide-react";
import { toast } from "sonner";
import { ConfirmButton } from "@/components/ConfirmDialog";
import { ResetPasswordDialog } from "@/components/PasswordDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Managers() {
  const { user, branch } = useAuth();
  const navigate = useNavigate();
  const [managers, setManagers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", password_confirm: "", phone: "", branch_id: "" });
  const [editing, setEditing] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", phone: "", active: true, branch_id: "" });
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [searchParams] = useSearchParams();
  const branchFilter = searchParams.get("branch");
  const load = useCallback(() => api.get("/managers", { params: branchFilter ? { branch: branchFilter } : {} }).then((r) => setManagers(r.data)), [branchFilter]);
  useEffect(() => {
    load();
    if (user.role === "super_admin") api.get("/branches").then((r) => setBranches(r.data));
  }, [load, user.role]);

  const create = async () => {
    try {
      if (form.password !== form.password_confirm) {
        return toast.error("Passwords do not match");
      }
      const { password_confirm, ...rest } = form;
      const payload = { ...rest, branch_id: user.role === "admin" ? branch.id : form.branch_id };
      await api.post("/managers", payload);
      toast.success("Manager created");
      setOpen(false);
      setForm({ name: "", email: "", password: "", password_confirm: "", phone: "", branch_id: "" });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const openEdit = (m) => {
    setEditing(m);
    setEditForm({ name: m.name, phone: m.phone || "", active: m.active, branch_id: m.branch_id || "" });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    try {
      const payload = { name: editForm.name, phone: editForm.phone, active: editForm.active };
      if (user.role === "super_admin") payload.branch_id = editForm.branch_id;
      await api.put(`/managers/${editing.id}`, payload);
      toast.success("Manager updated");
      setEditOpen(false); setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const toggleStatus = async (m) => {
    try {
      await api.put(`/managers/${m.id}`, { active: !m.active });
      toast.success(m.active ? "Manager deactivated" : "Manager activated");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (m) => {
    try {
      await api.delete(`/managers/${m.id}`);
      toast.success("Manager deleted");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const filtered = managers.filter((m) =>
    (m.name + m.email + (m.phone || "")).toLowerCase().includes(q.toLowerCase()) &&
    (statusFilter === "all" || (statusFilter === "active" ? m.active : !m.active))
  );

  return (
    <div className="animate-fade-up">
      <PageHeader title="Managers" subtitle="Channel partners managing client portfolios">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="add-manager-button" className="flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-blue-500 transition-colors">
              <Plus className="h-4 w-4" /> Add Manager
            </button>
          </DialogTrigger>
          <DialogContent className="card-surface">
            <DialogHeader><DialogTitle>Create Manager (Channel Partner)</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Field label="Full Name" testid="manager-name-input" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Field label="Email / Login ID" testid="manager-email-input" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <Field label="Mobile Number" testid="manager-phone-input" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              <Field label="Password" testid="manager-password-input" value={form.password} onChange={(v) => setForm({ ...form, password: v })} type="password" />
              <Field label="Confirm Password" testid="manager-password-confirm-input" value={form.password_confirm} onChange={(v) => setForm({ ...form, password_confirm: v })} type="password" />
              {user.role === "super_admin" && (
                <div>
                  <label className="text-sm font-medium">Assign to Branch</label>
                  <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                    <SelectTrigger data-testid="manager-branch-select" className="mt-1 bg-secondary border-input">
                      <SelectValue placeholder="Select branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.filter((b) => b.active).map((b) => (
                        <SelectItem key={b.id} value={b.id} data-testid={`branch-option-${b.id}`}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <button data-testid="manager-save-button" onClick={create} className="rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-blue-500 transition-colors">Create Manager</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative max-w-md flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="manager-search-input" placeholder="Search manager…" className="w-full rounded-lg bg-secondary border border-input pl-10 pr-3 py-2.5 text-sm outline-none focus:border-primary transition-colors" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="manager-status-filter" className="w-40 bg-secondary border-input"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="card-surface">
          <DialogHeader><DialogTitle>Edit Manager</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Full Name" testid="edit-manager-name" value={editForm.name} onChange={(v) => setEditForm({ ...editForm, name: v })} />
            <Field label="Phone" testid="edit-manager-phone" value={editForm.phone} onChange={(v) => setEditForm({ ...editForm, phone: v })} />
            {user.role === "super_admin" && (
              <div>
                <label className="text-sm font-medium">Branch</label>
                <Select value={editForm.branch_id} onValueChange={(v) => setEditForm({ ...editForm, branch_id: v })}>
                  <SelectTrigger data-testid="edit-manager-branch-select" className="mt-1 bg-secondary border-input"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => (
                      <SelectItem key={b.id} value={b.id} data-testid={`edit-branch-option-${b.id}`}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={editForm.active} data-testid="edit-manager-active" onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })} className="h-4 w-4 accent-blue-500" />
              Active
            </label>
          </div>
          <DialogFooter>
            <button data-testid="edit-manager-save" onClick={saveEdit} className="rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-blue-500 transition-colors">Save Changes</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="managers-grid">
        {filtered.map((m) => (
          <div key={m.id} onClick={() => navigate(`/managers/${m.id}`)} className="card-surface rounded-xl p-5 stat-glow cursor-pointer" data-testid={`manager-card-${m.id}`}>
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-full bg-gold/20 text-gold flex items-center justify-center font-semibold num">
                {initials(m.name)}
              </div>
              <div className="min-w-0">
                <div className="font-medium truncate">{m.name}</div>
                <div className="text-xs text-muted-foreground">{m.branch_name}</div>
              </div>
              <div className="ml-auto flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                <button onClick={() => toggleStatus(m)} data-testid={`manager-status-toggle-${m.id}`} title={m.active ? "Deactivate" : "Activate"} className={`text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1 ${m.active ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"}`}>
                  <Power className="h-3 w-3" /> {m.active ? "Active" : "Inactive"}
                </button>
                <ResetPasswordDialog userId={m.id} userName={m.name}
                  trigger={<button data-testid={`manager-reset-password-${m.id}`} className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-secondary row-hover"><KeyRound className="h-3.5 w-3.5" /></button>} />
                <button onClick={() => openEdit(m)} data-testid={`manager-edit-${m.id}`} className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-secondary row-hover">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <ConfirmButton
                  title="Delete manager?"
                  description={`This permanently deletes "${m.name}". Managers with clients must be transferred first.`}
                  confirmLabel="Delete Manager"
                  testid={`manager-delete-confirm-${m.id}`}
                  onConfirm={() => remove(m)}
                  trigger={
                    <button data-testid={`manager-delete-${m.id}`} className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 row-hover">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  }
                />
              </div>
            </div>
            <div className="mt-4 space-y-1.5 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> <span className="truncate">{m.email}</span></div>
              {m.phone && <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {m.phone}</div>}
            </div>
            <button onClick={(e) => { e.stopPropagation(); navigate(`/clients?manager=${m.id}`); }} data-testid={`manager-clients-link-${m.id}`} className="mt-4 pt-4 border-t border-border/60 flex items-center gap-2 text-sm w-full hover:text-primary row-hover">
              <UserRound className="h-4 w-4 text-primary" />
              <span className="num font-semibold">{m.client_count}</span>
              <span className="text-xs text-muted-foreground">Clients managed</span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <input
        type={type}
        value={value}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg bg-secondary border border-input px-3 py-2 text-sm outline-none focus:border-primary transition-colors"
      />
    </div>
  );
}
